import React, { lazy, Suspense, useLayoutEffect, useSyncExternalStore } from "react";

const RELOADED = "touki_chunk_reload";
// At most one reload in this long: a page that fails inside a shell that
// loads fine must not reload the tab over and over.
const RELOAD_WINDOW_MS = 30000;

function session(action) {
  try { return action(window.sessionStorage); } catch { return null; }
}

// How many pages are on their way, so the site's frame can leave its footer
// out until the page is there (App.js). Under the one-line loading status the
// footer painted right below the header, then jumped a screen down when the
// page arrived: a layout shift of 0.64 on a phone, with the footer's tagline
// as the largest paint. Counted in layout effects, so the footer is gone
// before the first paint and back in the same frame as the page.
let waiting = 0;
const listeners = new Set();
function setWaiting(change) {
  waiting += change;
  listeners.forEach((listener) => listener());
}
function subscribe(listener) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Counts the calling component as a page still on its way while it is shown. */
export function useMarkPageLoading() {
  useLayoutEffect(() => {
    setWaiting(1);
    return () => setWaiting(-1);
  }, []);
}

/** True while any page is still on its way. */
export function useIsPageLoading() {
  return useSyncExternalStore(subscribe, () => waiting > 0, () => false);
}

/** While a page downloads. */
export function PageLoading() {
  useMarkPageLoading();
  return React.createElement("p", { role: "status", "aria-live": "polite", className: "p-6 text-sm text-touki-muted" }, "…");
}

/**
 * A page downloaded when it is first shown, not with the home page.
 *
 * Each page waits in its own <Suspense>: navigating to one not yet
 * downloaded replaces the previous page with the loading line at once. Under
 * a shared boundary the router's transition kept the previous page on screen
 * meanwhile, and a booking button that had turned back on could be tapped
 * again: a second booking.
 *
 * After a deploy, a tab opened before it asks for chunk files the new build no
 * longer has; the server answers with the page shell and the import fails.
 * The tab reloads to pick up the new build, at most once in RELOAD_WINDOW_MS
 * (a timestamp, never cleared by an unrelated success); a failure within that
 * window is a real one and is thrown to the error screen.
 *
 * `preload()` starts the download ahead of a likely visit.
 */
export function lazyPage(load) {
  const Loaded = lazy(() => load().catch((error) => {
    const last = Number(session((s) => s.getItem(RELOADED))) || 0;
    const now = Date.now();
    if (now - last > RELOAD_WINDOW_MS && session((s) => { s.setItem(RELOADED, String(now)); return true; })) {
      window.location.reload();
      return new Promise(() => {});
    }
    throw error;
  }));
  function Page(props) {
    return React.createElement(Suspense, { fallback: React.createElement(PageLoading) }, React.createElement(Loaded, props));
  }
  Page.preload = () => load().catch(() => {});
  return Page;
}

/**
 * Fetch these pages once the browser is idle, so the first click on a search
 * does not wait for a download, unless the visitor asked to save data or is
 * on a slow connection, where every kilobyte they did not ask for costs them.
 */
export function preloadWhenIdle(pages) {
  const connection = typeof navigator !== "undefined" ? navigator.connection : null;
  if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || "")) return () => {};
  const run = () => pages.forEach((page) => page.preload?.());
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(run, { timeout: 5000 });
    return () => window.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(run, 2500);
  return () => window.clearTimeout(id);
}
